// useLogoFormLogic.ts
import { useState, useCallback, useEffect } from "react";

import { Business } from "0.Mine/3.Interfaces/business";

export interface LogoFormLogicProps {
  updateBusinessData: (data: Partial<Business>) => void;
  businessData: Partial<Business>;
}

export interface LogoFormLogicReturn {
  logo: File | null;
  banner: File | null;
  logoString: string | null;
  bannerString: string | null;
  handleLogoChange: (files: File) => void;
  handleBannerChange: (files: File) => void;
  handleLogoDelete: () => void;
  handleBannerDelete: () => void;
}

export const useLogoFormLogic = ({
  updateBusinessData,
  businessData,
}: LogoFormLogicProps): LogoFormLogicReturn => {
  const [logo, setLogo] = useState<File | null>(
    businessData.logoUrl instanceof File ? businessData.logoUrl : null
  );
  const [banner, setBanner] = useState<File | null>(
    businessData.coverUrl instanceof File ? businessData.coverUrl : null
  );

  const [logoString, setLogoString] = useState<string | null>(null);
  const [bannerString, setBannerString] = useState<string | null>(null);

  // Generate string URLs from Files when component loads
  useEffect(() => {
    if (logo instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoString(e.target?.result as string);
      };
      reader.readAsDataURL(logo);
    }
  }, [logo]);

  useEffect(() => {
    if (banner instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBannerString(e.target?.result as string);
      };
      reader.readAsDataURL(banner);
    }
  }, [banner]);

  const handleLogoChange = useCallback(
    (files: File) => {
      if (files) {
        setLogo(files);
        updateBusinessData({ logoUrl: files });
      }
    },
    [updateBusinessData]
  );

  const handleBannerChange = useCallback(
    (files: File) => {
      if (files) {
        setBanner(files);
        updateBusinessData({ coverUrl: files });
      }
    },
    [updateBusinessData]
  );

  const handleLogoDelete = useCallback(() => {
    setLogo(null);
    updateBusinessData({ logoUrl: null });
  }, [updateBusinessData]);

  const handleBannerDelete = useCallback(() => {
    setBanner(null);
    updateBusinessData({ coverUrl: null });
  }, [updateBusinessData]);

  return {
    logo,
    banner,
    bannerString,
    logoString,
    handleLogoChange,
    handleBannerChange,
    handleLogoDelete,
    handleBannerDelete,
  };
};
